import { TopNavigation } from '@amzn/awsui-components-react';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '../app/hooks';
import { useDrishtiPool } from '../config/drishtiPoolConfig';
import { clearDrishtiRefreshToken } from '../services/drishtiAuthService';
import { isDrishtiSession } from '../services/drishtiSession';
import { EventName, EventType, logDrishtiMetricEvent } from '../services/eventService';
import { clearDrishtiSession } from '../slice/drishtiUserInfoSlice';
import { buildSupportMailtoHref } from '../utils/drishtiSupportMail';
import type { SupportMailContext } from '../utils/drishtiSupportMail';

const i18nStrings = {
    overflowMenuTriggerText: 'More',
    overflowMenuTitleText: 'All',
    overflowMenuBackIconAriaLabel: 'Back',
    overflowMenuDismissIconAriaLabel: 'Close menu',
};

const identity = {
    href: '/drishti/site',
    title: 'Drishti Installer App',
};

const REPORT_PROBLEM_ID = 'report-problem';
const reportProblemItem = (context: SupportMailContext) => ({
    id: REPORT_PROBLEM_ID,
    text: 'Report a problem',
    href: buildSupportMailtoHref(context),
    external: true,
    externalIconAriaLabel: ' (opens in new tab)',
});

const DrishtiNavBar = () => {
    const dispatch = useDispatch();
    const pool = useDrishtiPool();
    const userInfo = useAppSelector((state) => state.drishtiUserData.session);
    const install = useAppSelector((state) => state.drishtiInstallConfig);

    // Same test as the route guards, so the menu never outlives access to the screens behind it.
    if (!isDrishtiSession(userInfo, pool?.userPoolId)) {
        return (
            <TopNavigation
                i18nStrings={i18nStrings}
                identity={identity}
                utilities={[{ type: 'button', ...reportProblemItem({}) }]}
            />
        );
    }

    const company = userInfo['custom:company'];
    const fullName = `${userInfo.given_name ?? ''} ${userInfo.family_name ?? ''}`.trim();

    // Clears only Drishti's slice; the install slice is reset by the next sign-in.
    const onSignOut = () => {
        logDrishtiMetricEvent({ eventName: EventName.DRISHTI_MANUAL_SIGN_OUT, eventType: EventType.COUNT });
        dispatch(clearDrishtiSession());
        clearDrishtiRefreshToken();
    };

    const items = [reportProblemItem({ session: userInfo, install }), { id: 'signout', text: 'Sign out' }];

    return (
        <TopNavigation
            i18nStrings={i18nStrings}
            identity={identity}
            utilities={[
                {
                    type: 'menu-dropdown',
                    text: fullName || userInfo.email,
                    description: company ? `${userInfo.email} [${company}]` : userInfo.email,
                    iconName: 'user-profile',
                    items,
                    onItemClick: (e) => {
                        if (e.detail.id === 'signout') {
                            onSignOut();
                        }
                        if (e.detail.id === REPORT_PROBLEM_ID) {
                            logDrishtiMetricEvent({
                                eventName: EventName.DRISHTI_SUPPORT_REPORT_OPENED,
                                eventType: EventType.COUNT,
                                deviceId: install.deviceSerial ?? undefined,
                                miscEventData: {
                                    status: install.calibration.status,
                                    phase: install.calibration.phase,
                                    escalated: install.calibration.escalated,
                                },
                            });
                        }
                    },
                },
            ]}
        />
    );
};

export default DrishtiNavBar;
